import { useParams, useNavigate } from "react-router";
import {
  Badge,
  Body,
  Heading,
  Icon,
  RowCard,
} from "@elaniintc/goes-design-system";
import {
  useGetFormalityRequestDetail,
  useGetFormalityRequestStatus,
} from "@/queries/formality-request";
import { useGetExecution } from "@/queries/flows/flows.api";
import { useGetInsolvencies } from "@/queries/users/insolvencies.api";
import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCommentText } from "@/utils/procedures-ui";
import type { FormalityRequestType } from "@/types/entities/formality-request";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";

export type Tramite = FormalityRequestType;

interface ResolutionFile {
  id?: string;
  file_name?: string;
  formatted_file_name?: string;
  name?: string;
  size?: number;
  url?: string;
  file_url?: string;
  link?: string;
  path?: string;
  is_signed?: boolean;
}

const formatBytes = (bytes?: number) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2))}${sizes[i]}`;
};

const getBaseFromInput = (val: unknown): string | undefined => {
  if (!val) return undefined;
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed === "object" && "base" in parsed) {
        return String(parsed.base);
      }
    } catch {
      // Not JSON string
    }
    return val;
  }
  if (typeof val === "object" && val !== null && "base" in val) {
    return String(val.base);
  }
  return undefined;
};

const getDocNodeFileName = (docNode: {
  inputs?: Array<{
    name: string;
    value: unknown;
  }>;
}): string => {
  const templateNameInput = docNode.inputs?.find(
    (input) => input?.name === "template_name",
  );
  if (templateNameInput?.value) {
    const base = getBaseFromInput(templateNameInput.value);
    if (base) return base;
  }

  const outputNameInput = docNode.inputs?.find(
    (input) => input?.name === "output_name",
  );
  if (outputNameInput?.value) {
    const base = getBaseFromInput(outputNameInput.value);
    if (base) return base;
  }

  const fileNameInput = docNode.inputs?.find(
    (input) => input?.name === "file_name",
  );
  if (fileNameInput?.value) {
    const base = getBaseFromInput(fileNameInput.value);
    if (base) return base;
  }

  return "Documento";
};

const ResolutionPage = () => {
  const { hasPermission } = useAuthorization();
  const canViewFlow = hasPermission(Permissions.flows.read);
  const params = useParams<{
    id?: string;
    registryId?: string;
    procedureId?: string;
  }>();
  const navigate = useNavigate();
  const tramiteId = params?.procedureId;
  const [stopStatusCheck, setStopStatusCheck] = useState(false);

  const { data: formalityRequestStatus } = useGetFormalityRequestStatus(
    tramiteId ?? "",
    !!tramiteId,
    stopStatusCheck ? 0 : 5000,
  );

  const {
    data: rawTramite,
    isLoading: isLoadingTramite,
    refetch: refetchTramite,
  } = useGetFormalityRequestDetail(tramiteId ?? "", stopStatusCheck);

  useEffect(() => {
    const status = formalityRequestStatus?.status;
    const isCompleted = status === "FINISHED" || status === "FAILED";
    // check that the data was fetched at least once before stopping the status check
    if (isCompleted && rawTramite) {
      setStopStatusCheck(true);
    }
  }, [formalityRequestStatus?.status, rawTramite]);

  const tramiteResponse = useMemo(() => {
    if (!rawTramite) return undefined;
    return { data: rawTramite as Tramite };
  }, [rawTramite]);

  const applicantId = rawTramite?.applicant_id ?? "";
  const { data: insolvenciesResponse } = useGetInsolvencies(applicantId);

  const hasBlockingInsolvencies = useMemo(() => {
    return (
      insolvenciesResponse?.data?.some(
        (insolvency) =>
          insolvency.status === "ACTIVE" ||
          insolvency.status === "APPEAL_SUBMITTED" ||
          insolvency.status === "APPEAL_REJECTED",
      ) ?? false
    );
  }, [insolvenciesResponse]);

  const redirectUrl = useMemo(() => {
    if (params.id && params.registryId) {
      return `/expedientes/entidad/${params.id}/detalle-registro/${params.registryId}/tramite/${tramiteId}`;
    }
    return `/tramite/${tramiteId}`;
  }, [params.id, params.registryId, tramiteId]);

  useEffect(() => {
    if (hasBlockingInsolvencies) {
      navigate(redirectUrl, { replace: true });
    }
  }, [hasBlockingInsolvencies, navigate, redirectUrl]);

  // Get flow execution to read resolution status from node inputs
  const flowExecutionId = tramiteResponse?.data?.flow_execution_id ?? "";
  const { data: executionResponse } = useGetExecution(
    flowExecutionId,
    flowExecutionId && !stopStatusCheck ? 5000 : 0,
    undefined,
    canViewFlow,
  );

  // Get resolution files from multiple sources
  const resolutionFiles = (() => {
    // First, try to get from tramite.resolution.files (for completed resolutions)
    if (tramiteResponse?.data?.resolution?.files?.length) {
      return tramiteResponse.data.resolution.files;
    }

    // If no files in resolution, get from resolution node inputs and document generator outputs
    const resolutionNode = executionResponse?.data?.node_executions?.find(
      (exec) => exec?.node?.ref_name === "resolution-node",
    );
    if (!resolutionNode) return [];

    // Get file IDs from resolution node inputs
    const fileInputs =
      resolutionNode.inputs?.filter((input) => input?.name === "files") || [];
    const fileIds = fileInputs
      .map((input) => input?.value)
      .filter((value) => value !== null && value !== undefined && value !== "");

    if (fileIds.length === 0) return [];

    // Find document generator nodes and match their outputs
    const documentGeneratorNodes =
      executionResponse?.data?.node_executions?.filter(
        (exec) =>
          exec?.node?.type === "DOCUMENT_GENERATOR" &&
          exec?.status === "COMPLETED",
      ) || [];

    const signDocumentNodes =
      executionResponse?.data?.node_executions?.filter(
        (exec) =>
          exec?.node?.ref_name === "sign-document-node" &&
          exec?.status === "COMPLETED",
      ) || [];
    const files: ResolutionFile[] = [];

    signDocumentNodes.forEach((signNode) => {
      signNode.outputs?.forEach((output) => {
        if (output?.value) {
          files.push({
            id: String(output.value),
            name: output.name || "Documento Firmado",
            file_name: `${output.name || "Documento_Firmado"}.pdf`,
            size: 0,
            is_signed: true,
          });
        }
      });
    });

    documentGeneratorNodes.forEach((docNode) => {
      const resultOutput = docNode.outputs?.find(
        (output) => output?.name === "result",
      ) as { name: string; value: string } | undefined;

      if (resultOutput && fileIds.includes(String(resultOutput.value))) {
        const fileName = getDocNodeFileName(docNode);

        files.push({
          id: String(resultOutput.value),
          name: fileName,
          file_name: `${fileName}.pdf`,
          size: 0, // Unknown size
        });
      }
    });

    const uniqueFiles = files.filter(
      (file, index, self) => index === self.findIndex((f) => f.id === file.id),
    );

    return uniqueFiles;
  })();

  const hasIncompleteFiles = resolutionFiles.some((file) => file.size === 0);

  useEffect(() => {
    if (hasIncompleteFiles) {
      const timer = setTimeout(() => {
        refetchTramite();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [hasIncompleteFiles, refetchTramite]);

  // Get resolution status from node execution inputs (same logic as ResolutionNodeCard)
  const getResolutionStatus = () => {
    const resolutionNode = executionResponse?.data?.node_executions?.find(
      (exec) => exec?.node?.ref_name === "resolution-node",
    );

    // Try to get from resolution node execution inputs
    if (resolutionNode?.inputs) {
      const statusInput = resolutionNode.inputs.find(
        (input) => input?.name === "status",
      ) as { name: string; value: string } | undefined;

      if (statusInput?.value) {
        return String(statusInput.value).toLowerCase();
      }
    }

    // Fallback to tramite.resolution.status or decision
    if (tramiteResponse?.data?.resolution?.status) {
      return String(tramiteResponse.data.resolution.status).toLowerCase();
    }

    if (tramiteResponse?.data?.resolution?.decision) {
      return String(tramiteResponse.data.resolution.decision).toLowerCase();
    }

    return "completed";
  };

  const status = getResolutionStatus();

  // Map status to badge configuration
  const getBadgeConfig = () => {
    switch (status) {
      case "approved":
      case "aprobado":
        return {
          variant: "success" as const,
          label: "Aprobado",
          icon: "check",
        };
      case "rejected":
      case "denegado":
      case "denied":
        return {
          variant: "danger" as const,
          label: "Denegado",
          icon: "xmark",
        };
      case "canceled":
      case "cancelado":
      case "desistido":
        return {
          variant: "neutral" as const,
          label: "Cancelado",
          icon: "ban",
        };
      case "unfair":
        return {
          variant: "neutral" as const,
          label: "Improcedente",
          icon: "ban",
        };
      default:
        return {
          variant: "success" as const,
          label: "Completado",
          icon: "check",
        };
    }
  };

  const badgeConfig = getBadgeConfig();

  if (!tramiteId) return null;

  if (isLoadingTramite) {
    return (
      <>
        <div className="flex items-center gap-2 justify-between border-line border-b border-neutral-silent pb-2 pl-2 min-w-0">
          <Skeleton className="h-7 w-96" />
        </div>
        <div className="p-4">
          <Skeleton className="h-6 w-64 mb-4" />
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </>
    );
  }

  const data = tramiteResponse?.data;

  // Get resolution description/comments
  const resolutionDescription = (() => {
    // First try from tramite.resolution
    if (data?.resolution?.comments || data?.resolution?.description) {
      return data.resolution.comments || data.resolution.description;
    }

    // If not, get from resolution node inputs
    const resolutionNode = executionResponse?.data?.node_executions?.find(
      (exec) => exec?.node?.ref_name === "resolution-node",
    );

    if (resolutionNode?.inputs) {
      const descriptionInput = resolutionNode.inputs.find(
        (input) => input?.name === "description",
      ) as { name: string; value: string } | undefined;

      return descriptionInput?.value || null;
    }

    return null;
  })();

  return (
    <div className="flex flex-col-reverse lg:flex-row gap-4 lg:gap-8 w-full min-w-0">
      <div className="flex flex-col gap-3 flex-1 min-w-0">
        <div className="flex flex-col gap-3">
          <div className="p-2">
            <div className="flex items-center justify-between w-full">
              <Heading variant="h6" className="text-heading">
                Documentos de resolución
              </Heading>
              <Badge size="sm" variant={badgeConfig.variant} shape="square">
                <span className="flex items-center">{badgeConfig.label}</span>
                <Icon type="fas" name={badgeConfig.icon} size="xs" />
              </Badge>
            </div>
            <Body className="text-body-quiet text-sm mt-1">
              Revisa y descarga los documentos generados en tu trámite.
            </Body>
          </div>
        </div>
        <div className="main-container">
          {resolutionDescription && (
            <div className="bg-surface-warning-accent-quiet border rounded-lg border-contextual-warning-quiet p-2 flex flex-col gap-1 mb-4">
              <Heading className="text-size-body-sm leading-2xl">
                Comentario general
              </Heading>
              <p className="font-body text-body text-size-body whitespace-pre-line text-justify wrap-break-word min-w-0">
                {formatCommentText(resolutionDescription)}
              </p>
            </div>
          )}
          {resolutionFiles.length === 0 && (
            <div className="p-2 text-body-quiet">No hay documentos.</div>
          )}

          <div className="flex flex-col gap-2">
            {hasIncompleteFiles ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (
              resolutionFiles.map((file: ResolutionFile, index) => {
                const key = file.id ?? `${file.file_name ?? "file"}-${index}`;
                const fileName =
                  file.formatted_file_name ?? file.name ?? "Documento";
                const subtitle = file.name ?? file.file_name ?? "";
                const sizeLabel = formatBytes(file.size);

                const href =
                  file.url ||
                  file.file_url ||
                  file.link ||
                  file.path ||
                  undefined;

                const card = (
                  <RowCard
                    title={
                      <div className="flex items-center gap-1 min-w-0">
                        <p className="font-normal text-base text-body-loud truncate leading-tight">
                          {fileName}
                        </p>
                      </div>
                    }
                    subtitle={subtitle}
                    color={"neutral"}
                    icon={{
                      name: "file",
                      type: "fas",
                      size: "lg",
                    }}
                  >
                    <RowCard.Right>
                      <Badge size="sm" variant={"neutral"} shape="pill">
                        <span className="flex items-center gap-1">
                          {sizeLabel}
                        </span>
                      </Badge>
                    </RowCard.Right>
                  </RowCard>
                );

                if (!href) {
                  return (
                    <div key={key} className="block">
                      {card}
                    </div>
                  );
                }

                return (
                  <div
                    key={key}
                    className="flex gap-2.5 flex-1 items-end flex-col-reverse xs:items-center xs:flex-row min-w-0"
                  >
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full xs:flex-1 min-w-0"
                    >
                      {card}
                    </a>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResolutionPage;
